/**
 * What the task pane and the "Fix issues" window say to each other.
 *
 * The pane sends the selected issues and the run's documents once the window
 * is ready, and the window answers with the issues the writer kept and the
 * documents to search, or with a cancel. The pane runs the fix pass and keeps
 * the window open: it sends each issue's status as the pass goes, and the
 * window shows it there, then offers the changes for review. Review opens in
 * the same window: the pane sends the review queue (``ReviewState``) and the
 * window sends back each Approve / Reject; once nothing is left it closes.
 */

import type { ResolveIssueDoc, SectionProposal } from "../../api/client";
import type { ReviewState, WindowToParent } from "./reviewProtocol";

export interface FixIssue {
  id: string;
  /** Flag type as the Issues tab shows it, e.g. "Missing info". */
  label: string;
  description: string;
}

export interface FixSection {
  module_id: string;
  number: string;
  title: string;
  issues: FixIssue[];
}

export interface FixDocument {
  id: string;
  name: string;
}

export interface FixSetup {
  sections: FixSection[];
  documents: FixDocument[];
}

/** Where the writer is in the steps, so Expand / Minimise carries it over. */
export interface FixDraft {
  step: number;
  unticked: string[];
  picked: string[];
}

export const EMPTY_FIX_DRAFT: FixDraft = { step: 0, unticked: [], picked: [] };

/** One issue's place in a running or finished fix pass. */
export type FixItemStatus =
  | "working"
  | "ready"
  | "approved"
  | "rejected"
  | "not_in_sources"
  | "not_placed"
  | "failed"
  | "stopped"
  | "no_change";

/** The pass as the window shows it, sent again on every change. */
export interface FixProgress {
  /** Status of each issue in the pass, by issue id. */
  status: Record<string, FixItemStatus>;
  /** The agent's newest action per section (module id) still being fixed. */
  live: Record<string, string>;
  /** The pass has ended (finished or stopped). */
  finished: boolean;
  /** Paused at the writer's request; Resume carries on with what is left. */
  paused: boolean;
  /** Why the pass could not start or carry on. */
  error?: string | null;
}

export const FIX_STATUS_LABEL: Record<FixItemStatus, string> = {
  working: "Fixing…",
  ready: "Change ready",
  approved: "Approved",
  rejected: "Rejected",
  not_in_sources: "Not in your sources",
  not_placed: "Couldn't place the change",
  failed: "Fix failed",
  stopped: "Stopped",
  no_change: "No change proposed",
};

/** Each issue's status from the pass state the pane holds. An issue whose
 *  section is still in flight is being fixed; once it has a verdict, that
 *  verdict; a finished pass with no verdict proposed nothing for it. */
export function fixStatusFor(
  issueId: string,
  moduleId: string,
  pass: {
    inFlight: ReadonlySet<string>;
    outcomes: Readonly<Record<string, { outcome: string }>>;
    finished: boolean;
  },
): FixItemStatus {
  const outcome = pass.outcomes[issueId]?.outcome;
  if (outcome === "proposed") return "ready";
  if (outcome === "not_in_sources" || outcome === "not_placed" || outcome === "failed" || outcome === "stopped") {
    return outcome;
  }
  if (pass.inFlight.has(moduleId) && !pass.finished) return "working";
  return pass.finished ? "no_change" : "working";
}

/** A "Change ready" issue once its change was decided: approved or rejected
 *  when the newest of its proposals was, still ready while any is pending, and
 *  ready too when none is known yet. ``proposals`` are the run's reviewable
 *  ones (superseded and no-op rows already dropped by the caller). */
export function withDecisions(
  status: Record<string, FixItemStatus>,
  proposals: readonly SectionProposal[],
): Record<string, FixItemStatus> {
  const out: Record<string, FixItemStatus> = {};
  for (const [id, v] of Object.entries(status)) {
    out[id] = v;
    if (v !== "ready") continue;
    const own = proposals.filter((p) => p.issue_ids.includes(id));
    if (own.length === 0 || own.some((p) => p.status === "pending")) continue;
    const when = (p: SectionProposal) => p.updated_at ?? p.created_at ?? "";
    const newest = own.reduce((a, b) => (when(b) > when(a) ? b : a));
    if (newest.status === "approved" || newest.status === "rejected") out[id] = newest.status;
  }
  return out;
}

/** The end-of-pass answer: fixed (a change is ready or approved), not fixed,
 *  still pending. */
export function fixTally(status: Record<string, FixItemStatus>): { fixed: number; notFixed: number; pending: number } {
  let fixed = 0;
  let notFixed = 0;
  let pending = 0;
  for (const v of Object.values(status)) {
    if (v === "ready" || v === "approved") fixed += 1;
    else if (v === "working" || v === "stopped") pending += 1;
    else notFixed += 1;
  }
  return { fixed, notFixed, pending };
}

/** "2 changes ready · 3 not in your sources", in the statuses' own order. */
export function fixSummaryLine(status: Record<string, FixItemStatus>): string {
  const counts = new Map<FixItemStatus, number>();
  for (const s of Object.values(status)) counts.set(s, (counts.get(s) ?? 0) + 1);
  const words: Record<FixItemStatus, [string, string]> = {
    working: ["being fixed", "being fixed"],
    ready: ["change ready", "changes ready"],
    approved: ["approved", "approved"],
    rejected: ["rejected", "rejected"],
    not_in_sources: ["not in your sources", "not in your sources"],
    not_placed: ["couldn't be placed", "couldn't be placed"],
    failed: ["failed", "failed"],
    stopped: ["stopped", "stopped"],
    no_change: ["no change proposed", "no change proposed"],
  };
  return (Object.keys(words) as FixItemStatus[])
    .filter((k) => counts.get(k))
    .map((k) => `${counts.get(k)} ${words[k][counts.get(k) === 1 ? 0 : 1]}`)
    .join(" · ");
}

export type FixParentToWindow =
  | { type: "setup"; setup: FixSetup; draft: FixDraft; progress?: FixProgress | null; review?: ReviewState | null }
  | { type: "progress"; progress: FixProgress }
  /** The pass's changes to review in this window (null: back to the pass). */
  | { type: "review"; review: ReviewState | null };

/** A click in the review step, as the review window would send it. */
export type FixReviewAction = Extract<
  WindowToParent,
  { type: "select" | "approve" | "reject" | "revise" | "approveAll" | "stopAll" | "undo" }
>;

export type FixWindowToParent =
  | { type: "ready" }
  | { type: "draft"; draft: FixDraft }
  | { type: "minimise" }
  | { type: "start"; issueIds: string[]; documentScope: ResolveIssueDoc[] }
  | { type: "cancel" }
  | { type: "pause" }
  | { type: "resume" }
  | { type: "stop" }
  | { type: "review" }
  | { type: "reviewAction"; action: FixReviewAction }
  | { type: "close" };

/** Flag types in the selection, most common first, with their issue ids. */
export function issueTypes(sections: FixSection[]): { label: string; ids: string[] }[] {
  const byLabel = new Map<string, string[]>();
  for (const s of sections) {
    for (const i of s.issues) {
      const ids = byLabel.get(i.label) ?? [];
      ids.push(i.id);
      byLabel.set(i.label, ids);
    }
  }
  return [...byLabel.entries()]
    .map(([label, ids]) => ({ label, ids }))
    .sort((a, b) => b.ids.length - a.ids.length || a.label.localeCompare(b.label));
}

/** A rough wall-clock estimate for the confirm step (one model call a section). */
export function fixDurationLabel(sectionCount: number, issueCount: number): string {
  const minutes = Math.max(1, Math.round((sectionCount * 40 + issueCount * 5) / 60));
  return minutes <= 1 ? "about a minute" : `about ${minutes} min`;
}
